<template>
  <div v-if="!hideButton">
    <BaseButton
      :title="$t('attributes.compose')"
      icon="envelope"
      inverse-icon-order
      @click="opened = true"
    />
  </div>
  <Dialog :open="opened" class="relative" @close="opened = false">
    <div class="fixed inset-0 z-999 bg-black/30" aria-hidden="true" />
    <div class="fixed inset-0 z-1000 flex w-screen items-center justify-center">
      <DialogPanel
        class="bg-surface-raised flex flex-col gap-2 rounded-lg p-4 transition-all"
        :class="{
          [modalWidth]: !fullScreen,
          'fixed inset-0': fullScreen,
          'h-[80vh] w-full': !fullScreen
        }"
      >
        <div class="flex shrink-0 flex-row">
          <DialogTitle class="flex-1 text-2xl font-semibold">
            {{ modalTitle }}
          </DialogTitle>
          <div class="flex flex-row gap-2">
            <button @click="fullScreen = !fullScreen">
              <FontAwesomeIcon
                :icon="[
                  'fal',
                  fullScreen ? 'arrows-minimize' : 'arrows-maximize'
                ]"
              />
            </button>
            <button @click="opened = false">
              <FontAwesomeIcon icon="times" />
            </button>
          </div>
        </div>
        <div class="grid min-h-0 flex-1 grid-cols-[auto_1fr_auto]">
          <div
            v-if="suggestedRecipients?.length"
            class="bg-surface-base col-start-1 flex min-h-0 w-70 flex-col gap-1 overflow-y-scroll rounded-md p-2"
          >
            <div
              v-for="suggestedRecipient in suggestedRecipients"
              :key="suggestedRecipient.label"
            >
              <h3 class="text-base font-bold capitalize-first">
                {{ $t(suggestedRecipient.label) }}
              </h3>
              <div class="flex flex-col gap-2">
                <RecipientCard
                  v-for="recipient in suggestedRecipient.recipients"
                  :key="recipient.id"
                  :recipient="recipient"
                  :disabled="alreadyInModelNotitications.includes(recipient.id)"
                  @add-to-to="addToTo"
                  @add-to-cc="addToCC"
                  @add-to-bcc="addToBCC"
                />
              </div>
            </div>
          </div>
          <div
            class="bg-surface-raised col-start-2 flex min-h-0 flex-col gap-4 overflow-y-scroll px-4"
          >
            <div class="min-h-0 flex-1">
              <ComposeForm
                :model-notification="modelNotification"
                :model-type="computedModelType"
                :model-id="computedModelId"
                :suggested-contacts="suggestedRecipients"
                :send-record="invoiceRecord"
                :initial-subject="initialSubject"
                @close="opened = false"
                @sended="sendedHandler"
                @saved="savedHandler"
                @reset="resetModelNotification"
                @add-attachment="addTemplateAttachable"
              >
                <!-- A reply from the invoice's mail history attaches no PDF,
                     so it starts no send record. -->
                <template
                  v-if="shouldCreateInvoiceRecord && invoiceRecord"
                  #extra-content
                >
                  <InvoiceReminderPanel :record="invoiceRecord" />
                </template>
              </ComposeForm>
            </div>
          </div>
          <div
            v-if="
              templateSuggestedAttachments?.length ||
              dossierSuggestedAttachments?.length ||
              dossierDocumentSuggestedAttachments?.length
            "
            class="bg-surface-base col-start-3 flex min-h-0 w-70 flex-col gap-2 overflow-y-scroll rounded-md p-4"
          >
            <template v-if="templateSuggestedAttachments?.length">
              <h3 class="text-base font-bold capitalize-first">
                {{ $t('attributes.messageText') }}
              </h3>
              <div class="flex flex-col gap-2">
                <AttachmentComponent
                  v-for="attachment in templateSuggestedAttachments"
                  :key="attachment.id"
                  :attachment="attachment"
                  :already-attached-attachments="
                    alreadyAttachedAttachments.includes(attachment.id)
                  "
                  @add-attachment="() => addAttachment(attachment)"
                  @remove-attachment="() => removeAttachment(attachment.id)"
                />
              </div>
            </template>
            <template v-if="dossierSuggestedAttachments?.length">
              <h3 class="text-base font-bold capitalize-first">
                {{ $t('global.attachments') }}
              </h3>
              <div class="flex flex-col gap-2">
                <AttachmentComponent
                  v-for="attachment in dossierSuggestedAttachments"
                  :key="attachment.id"
                  :attachment="attachment"
                  :already-attached-attachments="
                    alreadyAttachedAttachments.includes(attachment.id)
                  "
                  @add-attachment="() => addAttachment(attachment)"
                  @remove-attachment="() => removeAttachment(attachment.id)"
                />
              </div>
            </template>
            <template v-if="dossierDocumentSuggestedAttachments?.length">
              <h3 class="text-base font-bold capitalize-first">
                {{ $t('attributes.dossier') }}
              </h3>
              <div class="flex flex-col gap-2">
                <AttachmentComponent
                  v-for="attachment in dossierDocumentSuggestedAttachments"
                  :key="attachment.id"
                  :attachment="attachment"
                  :already-attached-attachments="
                    alreadyAttachedAttachments.includes(attachment.id)
                  "
                  @add-attachment="() => addAttachment(attachment)"
                  @remove-attachment="() => removeAttachment(attachment.id)"
                />
              </div>
            </template>
          </div>
        </div>
      </DialogPanel>
    </div>
  </Dialog>
</template>

<script>
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/vue'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import ComposeForm from '@c/Mail/component/ComposeForm.vue'
import { mapActions, mapGetters } from 'vuex'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import AttachmentComponent from '@/components/Mail/component/AttachmentComponent.vue'
import RecipientCard from '@c/Mail/component/RecipientCard.vue'
import InvoiceReminderPanel from '@c/Media/InvoiceReminderPanel.vue'

const createBaseEmailObject = () => ({
  from: null,
  to: [],
  cc: [],
  bcc: [],
  subject: '',
  message: '',
  model_id: null,
  model_type: null,
  email_attachments: [],
  files: [],
  attachables: [],
  send_copy_to_agency: false
})

export default {
  name: 'ModalComposeMail',
  components: {
    InvoiceReminderPanel,
    RecipientCard,
    FontAwesomeIcon,
    DialogTitle,
    ComposeForm,
    BaseButton,
    DialogPanel,
    Dialog,
    AttachmentComponent
  },
  props: {
    title: {
      type: String,
      required: true
    },
    modelType: {
      type: String,
      required: false,
      default: null
    },
    modelId: {
      type: String,
      required: false,
      default: null
    },
    dossierId: {
      type: String,
      required: false,
      default: null
    },
    modelNotificationId: {
      type: String,
      required: false,
      default: null
    },
    hideButton: {
      type: Boolean,
      required: false,
      default: false
    },
    defaultSignature: {
      type: String,
      required: false,
      default: null
    },
    defaultMessageText: {
      type: String,
      required: false,
      default: null
    },
    groupId: {
      type: String,
      required: false,
      default: null
    },
    responseType: {
      type: String,
      required: false,
      default: null,
      validator: (value) =>
        value === null || ['answer', 'answer_all', 'share'].includes(value)
    }
  },
  emits: ['update:opened', 'sended', 'saved'],
  data() {
    return {
      fullScreen: false,
      opened: false,
      // Incremented on every opening: a response started by a previous opening is stale.
      openToken: 0,
      modelNotification: {
        ...createBaseEmailObject(),
        //pourquoi se basé sur le dossierId pour envoyer le type de model au back, si on a un modelType Invoice, on envoie quand meme le dossier, => les variables de l'invoice ne sont plus récupérés
        // model_id: this.dossierId ? this.dossierId : this.modelId,
        // model_type: this.dossierId ? 'dossier' : this.modelType
        model_id: this.modelId,
        model_type: this.modelType
      },
      suggestedRecipients: [],
      suggestedAttachments: [],
      invoiceRecord: null,
      // Objet tel que le back l'a renvoyé à l'ouverture : sert de référence pour
      // savoir si l'utilisateur a modifié l'objet à la main (BBSW-10755).
      initialSubject: null,
      overrideSubject: null,
      overrideBody: null
    }
  },
  computed: {
    ...mapGetters({
      auth: 'auth/getAuth',
      invoice: 'invoicing/getInvoice'
    }),
    computedModelId() {
      //pourquoi se baser sur le dossierId pour envoyer le type de model au back, si on a un modelType Invoice, on envoie quand meme le dossier, => les variables de l'invoice ne sont plus récupérés
      return this.modelId
    },
    computedModelType() {
      return this.modelType
    },
    modalTitle() {
      return [this.$t('global.email'), this.title].filter((x) => x).join(' - ')
    },
    modalWidth() {
      if (
        this.suggestedRecipients.length > 0 &&
        this.suggestedAttachments.length > 0
      ) {
        return 'max-w-7xl xl:max-w-[90rem]'
      } else if (
        this.suggestedRecipients.length > 0 ||
        this.suggestedAttachments.length > 0
      ) {
        return 'max-w-6xl xl:max-w-7xl'
      }
      return 'max-w-5xl  xl:max-w-6xl'
    },
    alreadyAttachedAttachments() {
      return this.modelNotification.attachables
        .filter(
          (attachment) =>
            attachment.type === 'media' ||
            attachment.type === 'templateAttachment' ||
            attachment.type === 'document'
        )
        .map((attachment) => attachment.id)
    },
    alreadyInModelNotitications() {
      return [
        ...this.modelNotification.to.map((contact) => contact.id),
        ...this.modelNotification.cc.map((contact) => contact.id),
        ...this.modelNotification.bcc.map((contact) => contact.id)
      ]
    },
    dossierSuggestedAttachments() {
      return this.suggestedAttachments.filter(
        (attachment) =>
          attachment.type !== 'templateAttachment' &&
          attachment.type !== 'document'
      )
    },
    templateSuggestedAttachments() {
      return this.suggestedAttachments.filter(
        (attachment) => attachment.type === 'templateAttachment'
      )
    },
    dossierDocumentSuggestedAttachments() {
      return this.suggestedAttachments.filter(
        (attachment) => attachment.type === 'document'
      )
    },
    shouldCreateInvoiceRecord() {
      return (
        this.modelType === 'invoice' &&
        this.invoice &&
        this.invoice.doc_type === 'INV' &&
        !this.$route.params?.payment_id
      )
    }
  },
  watch: {
    opened(newValue, oldValue) {
      // Attachables are cleared on close rather than on open: callers push them through
      // addAttachable() right before openModal(), so clearing on open would drop them.
      if (newValue !== oldValue && !newValue) {
        this.modelNotification.attachables = []
        return
      }

      if (newValue !== oldValue && newValue) {
        this.openToken++
        const openToken = this.openToken
        this.suggestedAttachments = []
        this.getMetadataFromModel(openToken)

        if (this.modelNotificationId) {
          this.loadExistingDraft(openToken)
          return
        }

        if (this.groupId && this.responseType) {
          this.loadResponseDraft(openToken)
          return
        }

        this.processFetchNewNewModelNotificationAction({
          params: {
            model_id: this.modelId,
            model_type: this.modelType,
            dossier_id: this.dossierId,
            defaultSignature: this.defaultSignature,
            default_message_text: this.defaultMessageText
          }
        })
          .then((response) => {
            // The modal may have been closed (and reopened) before the response arrived:
            // applying it would leak into the current opening.
            if (this.isStaleResponse(openToken)) {
              return
            }

            const prefilledTo = [...this.modelNotification.to]
            Object.assign(this.modelNotification, response.model_notification)
            // Préserver les destinataires pré-remplis si le backend n'en fournit pas
            if (prefilledTo.length > 0 && !this.modelNotification.to?.length) {
              this.modelNotification.to = prefilledTo
            }
            // Appliquer les overrides subject/body (ex: commandes Enaos)
            if (this.overrideSubject) {
              this.modelNotification.subject = this.overrideSubject
              this.overrideSubject = null
            }
            if (this.overrideBody) {
              this.modelNotification.message = this.overrideBody
              this.overrideBody = null
            }
            this.initialSubject = this.modelNotification.subject ?? ''

            if (response.templateAttachments) {
              response.templateAttachments.forEach((attachment) => {
                this.addTemplateAttachable(attachment)
              })
            }
          })
          .catch(() => {
            this.overrideSubject = null
            this.overrideBody = null
          })
      }
    }
  },
  mounted() {},
  methods: {
    ...mapActions({
      processGetMetadataFromModelAction:
        'modelNotification/getMetadataFromModel',
      processFetchNewNewModelNotificationAction:
        'modelNotification/fetchNewNewModelNotification',
      processFetchThreadByGroupIdForAnswerAction:
        'modelNotification/fetchThreadByGroupIdForAnswer',
      processFetchModelNotificationByIdAction:
        'modelNotification/fetchModelNotificationById'
    }),
    // utiliser via des $refs (ne pas supprimer)
    addAttachable(type, id, title = null, object = {}, printOptions = {}) {
      this.modelNotification.attachables.push({
        type,
        id,
        title: title ?? `#${id}`,
        object,
        printOptions
      })
    },
    addTemplateAttachable(attachable) {
      this.suggestedAttachments.push(attachable)
      this.addAttachable(
        attachable.type,
        attachable.id,
        attachable.title,
        attachable
      )
    },
    addFile(blob, filename = null, properties = null) {
      const file = new File([blob], filename ?? 'capture.jpg', {
        type: blob.type || 'image/jpeg'
      })
      this.modelNotification.files = [
        {
          file: file,
          properties: properties
        }
      ]

      if (this.shouldCreateInvoiceRecord) {
        this.invoiceRecord = {
          sender_id: this.auth.id,
          send_type: null,
          send_method: 'email',
          invoice_id: this.invoice.id
        }
      }
    },
    // utiliser via des $refs (ne pas supprimer)
    openModal() {
      this.opened = true
    },
    isStaleResponse(openToken) {
      return !this.opened || openToken !== this.openToken
    },
    loadResponseDraft(openToken) {
      this.processFetchThreadByGroupIdForAnswerAction({
        groupId: this.groupId,
        type: this.responseType
      }).then((response) => {
        const notifications = response?.model_notifications ?? []
        if (this.isStaleResponse(openToken) || !notifications[0]) {
          return
        }

        // Clone the server-returned draft before mutating it (avoid mutating store state).
        const draft = { ...notifications[0] }
        const previousMessage = notifications[1]
        draft.message = this.buildQuotedBody(previousMessage)

        // Object.assign carries draft.email_attachments (copied by the backend for
        // forwards) onto modelNotification, which the template already renders — do not
        // also push them into attachables or they would be duplicated.
        Object.assign(this.modelNotification, draft)
        this.initialSubject = this.modelNotification.subject ?? ''
      })
    },
    // A draft saved beforehand (by the AI assistant, for one): its id makes
    // ComposeForm save and send that same draft instead of creating another.
    loadExistingDraft(openToken) {
      this.processFetchModelNotificationByIdAction({
        id: this.modelNotificationId
      })
        .then((response) => {
          if (
            this.isStaleResponse(openToken) ||
            !response?.model_notification
          ) {
            return
          }

          // email_attachments come along, the template renders them as they are
          Object.assign(this.modelNotification, response.model_notification)
          this.initialSubject = this.modelNotification.subject ?? ''
        })
        // Draft gone (deleted meanwhile): the client already toasted, close
        // rather than leave an empty mail that would be sent as a new one
        .catch(() => {
          this.opened = false
        })
    },
    buildQuotedBody(previousMessage) {
      if (!previousMessage) {
        return ''
      }

      const author = previousMessage.from?.name ?? ''
      const date = previousMessage.send_date
        ? this.$h.formatDate(previousMessage.send_date, 'LLL')
        : ''
      const body = previousMessage.message ?? ''

      return `<br><br><blockquote style="border-left:2px solid #ccc;padding-left:8px;color:#666">${author} — ${date}<br>${body}</blockquote>`
    },
    getMetadataFromModel(openToken) {
      // a mail with no document behind it (a note of commission, a new message) has nothing to suggest
      if (!this.modelType || !this.modelId) {
        return
      }

      this.processGetMetadataFromModelAction({
        model_id: this.modelId,
        model_type: this.modelType,
        dossier_id: this.dossierId
      }).then((response) => {
        if (this.isStaleResponse(openToken)) {
          return
        }

        this.suggestedRecipients = response?.suggested_recipients ?? []
        this.suggestedAttachments = this.suggestedAttachments.concat(
          response?.suggested_attachments ?? []
        )
      })
    },
    addAttachment(attachment) {
      this.modelNotification.attachables.push(attachment)
    },
    removeAttachment(id) {
      this.modelNotification.attachables =
        this.modelNotification.attachables.filter(
          (attachment) => attachment.id !== id
        )
    },
    openWithOverrides({ subject, body } = {}) {
      this.overrideSubject = subject ?? null
      this.overrideBody = body ?? null
    },
    addToTo(recipient) {
      this.modelNotification.to.push(recipient)
    },
    addToCC(recipient) {
      this.modelNotification.cc.push(recipient)
    },
    addToBCC(recipient) {
      this.modelNotification.bcc.push(recipient)
    },
    configureMail() {
      console.log(this.auth)
    },
    resetModelNotification() {
      this.initialSubject = null
      this.modelNotification = {
        ...createBaseEmailObject(),
        model_id: this.modelId,
        model_type: this.modelType
      }
    },
    sendedHandler() {
      // who the mail went to, for the pages that keep a trace of the sending
      const receivers = [
        ...(this.modelNotification.to ?? []),
        ...(this.modelNotification.cc ?? []),
        ...(this.modelNotification.bcc ?? [])
      ]
        .map((recipient) =>
          typeof recipient === 'string' ? recipient : recipient?.email
        )
        .filter(Boolean)

      this.$emit('sended', { receivers })
      this.resetModelNotification()
      this.opened = false
    },
    savedHandler() {
      this.$emit('saved')
      this.resetModelNotification()
      this.opened = false
    }
  }
}
</script>
